import { TemporalFormatter } from '@/components/TemporalFormatter';
import { emailIcon, phoneIcon } from '@/imports/icons';
import type { Lead } from '@/models/entities/lead';

interface LeadPartiesProps {
  readonly selectedLead: Lead;
}

function PhoneIcon() {
  return (
    <span className="relative shrink-0 size-4" aria-hidden="true">
      <svg
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 16 16"
        focusable="false"
        aria-hidden="true"
      >
        <path d={phoneIcon} fill="black" />
      </svg>
    </span>
  );
}

function EmailIcon() {
  return (
    <span className="overflow-clip relative shrink-0 size-4" aria-hidden="true">
      <svg
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 16 16"
        focusable="false"
        aria-hidden="true"
      >
        <path d={emailIcon} fill="black" />
      </svg>
    </span>
  );
}

export function LeadParties({ selectedLead }: LeadPartiesProps) {
  const customerName = selectedLead.customer.name;
  const customerPhone = selectedLead.customer.contactPhone;
  const contactPerson = selectedLead.customer.contactName;
  const vin = selectedLead.vin;
  const scheduledDate = selectedLead.workflow.scheduledDate;
  const dealerName = selectedLead.maintenance.sellingDealerId.name;
  const dueDate = selectedLead.maintenance.dueDate;
  const dueMileage = selectedLead.maintenance.dueMileage;
  const dueMileageDisplay = dueMileage
    ? `${dueMileage.value} ${dueMileage.unit}`
    : '—';

  return (
    <div className="text-xs grid grid-cols-3 gap-6 mb-6">
      <section
        aria-labelledby="customer-details-heading"
        className="text-xs bg-white rounded-lg p-6"
      >
        <h3
          id="customer-details-heading"
          className="text-base font-medium mb-2"
        >
          CUSTOMER DETAILS
        </h3>
        <div className="space-y-2">
          <address className="not-italic">
            <p className="font-semibold">{customerName}</p>
          </address>
          {!selectedLead.customer.isUnknown && (
            <ul
              className="flex flex-row gap-2 pt-1 list-none p-0 m-0"
              aria-label="Customer contact options"
            >
              {customerPhone && (
                <li>
                  <a
                    href={`tel:${customerPhone}`}
                    className="flex items-center gap-2 border border-[#5f5e5b] px-2 py-2 rounded hover:bg-gray-50 h-8 whitespace-nowrap"
                    aria-label={`Call ${customerName} at ${customerPhone}`}
                  >
                    <PhoneIcon />
                    <span className="uppercase">{customerPhone}</span>
                  </a>
                </li>
              )}
              {contactPerson && (
                <li>
                  <span className="flex items-center gap-2 border border-[#5f5e5b] px-2 py-2 rounded h-8 whitespace-nowrap">
                    <EmailIcon />
                    <span className="uppercase">{contactPerson}</span>
                  </span>
                </li>
              )}
            </ul>
          )}
        </div>
      </section>

      <section
        aria-labelledby="vehicle-service-heading"
        className="bg-white rounded-lg p-6"
      >
        <h3 id="vehicle-service-heading" className="text-base font-medium mb-2">
          VEHICLE & SERVICE
        </h3>
        <dl className="space-y-1">
          <div className="flex gap-2">
            <dt className="font-medium text-black">VIN</dt>
            <dd className="text-gray-600">{vin}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium text-black">Due Date</dt>
            <dd className="text-gray-600">
              <TemporalFormatter
                value={dueDate}
                options={{
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }}
              />
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium text-black">Due Mileage</dt>
            <dd className="text-gray-600">{dueMileageDisplay}</dd>
          </div>
          {scheduledDate && (
            <div className="flex gap-2">
              <dt className="font-medium text-black">Scheduled</dt>
              <dd className="text-gray-600">
                <TemporalFormatter value={scheduledDate} />
              </dd>
            </div>
          )}
        </dl>
      </section>

      <section
        aria-labelledby="selling-dealer-heading"
        className="bg-white rounded-lg p-6"
      >
        <h3 id="selling-dealer-heading" className="text-base font-medium mb-2">
          SELLING DEALER
        </h3>
        <p className="font-semibold mb-1">{dealerName}</p>
      </section>
    </div>
  );
}
